import type { Credentials, S3Bucket } from "modules/objectStorage/types";
import type {
  BackupStorageContext,
  BackupStorageKeyContext,
  BackupBucketCreateParams,
  BackupStoragePicker
} from "./types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ChangeDatabaseStatusParams,
  ChangeDatabaseStatusResponse,
  CreateBackupDatabaseParams,
  CreateBackupDatabaseResponse,
  CreateDatabaseChildParams,
  CreateDatabaseChildResponse,
  CreateDatabaseParams,
  CreateDatabaseResponse,
  CreateDatabaseUserParams,
  CreateDatabaseUserResponse,
  CreateReplicaDatabaseParams,
  CreateReplicaDatabaseResponse,
  CronBackupParams,
  CronBackupResponse,
  CronDeleteParams,
  CronDeleteResponse,
  BackupSet,
  DBManagementUrl,
  DBServiceParams,
  Database,
  DatabaseBackupFile,
  DatabaseBackupStatus,
  DatabaseChild,
  DatabaseCustomOptions,
  DatabaseEncodings,
  DatabaseMetrics,
  DatabaseResetPassword,
  DatabaseUser,
  DbaasQuotas,
  DeleteDatabaseChildParams,
  DeleteDatabaseParams,
  DeleteDatabaseResponse,
  DeleteDatabaseUserParams,
  GetDBServiceParamsParams,
  GetDBServiceParamsResponse,
  GetDBServicesParamsParams,
  GetDBServicesParamsResponse,
  GetDatabaseBackupFilesResponse,
  GetDatabaseBackupStatusResponse,
  GetDatabaseChildrenParams,
  GetDatabaseChildrenResponse,
  GetDatabaseCustomOptionsParams,
  GetDatabaseCustomOptionsResponse,
  GetDatabaseEncodingsParams,
  GetDatabaseEncodingsResponse,
  GetDatabaseMetricsParams,
  GetDatabaseMetricsResponse,
  GetDatabaseParams,
  GetDatabaseResponse,
  GetDatabaseUsersParams,
  GetDatabaseUsersResponse,
  GetDatabasesParams,
  GetDatabasesResponse,
  GetDbaasQuotasParams,
  GetDbaasQuotasResponse,
  GetManagementUrlParams,
  GetManagementUrlResponse,
  GetReplicaDatabaseParams,
  GetReplicaDatabasesResponse,
  ResetDatabasePasswordParams,
  ResetDatabasePasswordResponse,
  ResetDatabaseUserPasswordParams,
  ResetDatabaseUserPasswordResponse,
  RestoreDatabaseParams,
  RestoreDatabaseResponse,
  RollbackRestoreParams,
  RollbackRestoreResponse,
  S3LinkDatabaseParams,
  S3LinkDatabaseResponse,
  S3UnlinkDatabaseParams,
  S3UnlinkDatabaseResponse,
  SetDatabaseUserPermissionsParams,
  UpdateDatabaseChildParams,
  UpdateDatabaseCustomOptionsParams,
  UpdateDatabaseCustomOptionsResponse,
  UpdateDatabaseParams,
  UpdateDatabaseResponse
} from "./types";

export type DatabasesState = {
  backupFilesError: string | null;
  backupScheduleError: string | null;
  backupScheduleSaved: boolean;
  backupStoragePicker: BackupStoragePicker;
  s3LinkError: string | null;
  s3LinkSuccessful: boolean;
  databaseEncodings: DatabaseEncodings | null;
  database: Database | null;
  databaseResetPassword: DatabaseResetPassword | null;
  replicaDatabase: Database | null;
  databases: Database[] | null;
  replicaDatabases: Database[] | null;
  databaseBackupStatus: DatabaseBackupStatus | null;
  databaseBackupFiles: DatabaseBackupFile[] | null;
  databaseBackupSets: BackupSet[] | null;
  dbManagementUrl: DBManagementUrl | null;
  DBServiceParams: DBServiceParams | null;
  DBServicesParams: DBServiceParams[] | null;
  isDatabaseLoading: boolean;
  dbaasQuotas: DbaasQuotas | null;
  areDbaasQuotasLoading: boolean;
  isDatabaseBackupStatusLoading: boolean;
  areDatabaseBackupFilesLoading: boolean;
  isDatabaseRestoring: boolean;
  isDatabaseRollingBack: boolean;
  isDatabaseBackingUp: boolean;
  isDatabaseS3Linking: boolean;
  isDatabaseS3Unlinking: boolean;
  areDatabasesLoading: boolean;
  areReplicaDatabasesLoading: boolean;
  isCronBackupUpdating: boolean;
  isCronBackupDeleting: boolean;
  isReplicaDatabaseCreating: boolean;
  isDatabaseCreating: boolean;
  isDatabaseUpdating: boolean;
  isDatabaseDeleting: boolean;
  isDatabaseGettingManagementUrl: boolean;
  areDBServiceParamsLoading: boolean;
  areDBServicesParamsLoading: boolean;
  isDatabaseStatusChanging: boolean;
  isDatabasePasswordResetting: boolean;
  isDatabaseCustomOptionsLoading: boolean;
  databaseCustomOptions: DatabaseCustomOptions[] | null;
  isDatabaseCustomOptionsUpdating: boolean;
  databaseChildren: DatabaseChild[] | null;
  databaseUsers: DatabaseUser[] | null;
  databaseUser: Database | null;
  isDatabaseChildrenLoading: boolean;
  isDatabaseChildrenCreating: boolean;
  isDatabaseChildrenDeleting: boolean;
  isDatabaseChildUpdating: boolean;
  isDatabaseUsersLoading: boolean;
  isDatabaseUsersCreating: boolean;
  isDatabaseUsersDeleting: boolean;
  isDatabaseUsersPermissionsUpdating: boolean;
  isDatabaseEncodingsLoading: boolean;
  createdDatabasePassword: string | null;
  databaseMetrics: DatabaseMetrics | null;
  isDatabaseMetricsLoading: boolean;
};

const initialBackupStoragePicker: BackupStoragePicker = {
  context: null,
  credentialId: "",
  credentials: null,
  details: null,
  buckets: null,
  createdCredentials: null,
  createdBucketName: null,
  loadingKeys: false,
  loadingDetails: false,
  loadingBuckets: false,
  creatingKey: false,
  creatingBucket: false,
  errors: {
    keys: null,
    details: null,
    buckets: null,
    createKey: null,
    createBucket: null
  }
};
const matchesBackupPicker = (
  state: DatabasesState,
  context: BackupStorageContext
) =>
  state.backupStoragePicker.context?.sessionId === context.sessionId &&
  state.backupStoragePicker.context.regionId === context.regionId &&
  state.backupStoragePicker.context.projId === context.projId;

const initialState: DatabasesState = {
  backupFilesError: null,
  backupScheduleError: null,
  backupScheduleSaved: false,
  backupStoragePicker: initialBackupStoragePicker,
  database: null,
  databaseResetPassword: null,
  replicaDatabase: null,
  databases: null,
  replicaDatabases: null,
  databaseBackupStatus: null,
  databaseBackupFiles: null,
  databaseBackupSets: null,
  dbManagementUrl: null,
  DBServiceParams: null,
  DBServicesParams: null,
  isDatabaseLoading: false,
  dbaasQuotas: null,
  areDbaasQuotasLoading: false,
  isDatabaseBackupStatusLoading: false,
  areDatabaseBackupFilesLoading: false,
  isDatabaseRestoring: false,
  isDatabaseRollingBack: false,
  isDatabaseBackingUp: false,
  s3LinkError: null,
  s3LinkSuccessful: false,
  isDatabaseS3Linking: false,
  isDatabaseS3Unlinking: false,
  isCronBackupUpdating: false,
  isCronBackupDeleting: false,
  areDatabasesLoading: false,
  areReplicaDatabasesLoading: false,
  isReplicaDatabaseCreating: false,
  isDatabaseCreating: false,
  isDatabaseUpdating: false,
  isDatabaseDeleting: false,
  isDatabaseGettingManagementUrl: false,
  areDBServiceParamsLoading: false,
  areDBServicesParamsLoading: false,
  isDatabaseStatusChanging: false,
  isDatabasePasswordResetting: false,
  isDatabaseCustomOptionsLoading: false,
  databaseCustomOptions: null,
  isDatabaseCustomOptionsUpdating: false,
  databaseChildren: null,
  databaseUsers: null,
  databaseUser: null,
  databaseEncodings: null,
  isDatabaseChildrenLoading: false,
  isDatabaseChildrenCreating: false,
  isDatabaseChildrenDeleting: false,
  isDatabaseChildUpdating: false,
  isDatabaseUsersLoading: false,
  isDatabaseUsersCreating: false,
  isDatabaseUsersDeleting: false,
  isDatabaseUsersPermissionsUpdating: false,
  isDatabaseEncodingsLoading: false,
  createdDatabasePassword: null,
  databaseMetrics: null,
  isDatabaseMetricsLoading: false
};

const databasesSlice = createSlice({
  name: "databases",
  initialState,
  reducers: {
    // Get database
    getDatabaseRequest: (state, action: PayloadAction<GetDatabaseParams>) => {
      state.isDatabaseLoading = true;
    },
    getDatabaseSuccess: (state, action: PayloadAction<GetDatabaseResponse>) => {
      state.database = action.payload;
      state.isDatabaseLoading = false;
    },
    getDatabaseFailure: (
      state,
      action: PayloadAction<{ params: GetDatabaseParams; error: string }>
    ) => {
      state.isDatabaseLoading = false;
    },

    // Get databases
    getDatabasesRequest: (state, action: PayloadAction<GetDatabasesParams>) => {
      state.areDatabasesLoading = true;
    },
    getDatabasesSuccess: (
      state,
      action: PayloadAction<GetDatabasesResponse>
    ) => {
      state.databases = action.payload;
      state.areDatabasesLoading = false;
    },
    getDatabasesFailure: (
      state,
      action: PayloadAction<{ params: GetDatabasesParams; error: string }>
    ) => {
      state.areDatabasesLoading = false;
      state.databases ??= [];
    },

    // Create database
    createDatabaseRequest: (
      state,
      action: PayloadAction<CreateDatabaseParams>
    ) => {
      state.isDatabaseCreating = true;
    },
    createDatabaseSuccess: (
      state,
      action: PayloadAction<CreateDatabaseResponse>
    ) => {
      state.isDatabaseCreating = false;
      state.database = action.payload;
      state.createdDatabasePassword = action.payload.password;
    },
    createDatabaseFailure: (
      state,
      action: PayloadAction<{ params: CreateDatabaseParams; error: string }>
    ) => {
      state.isDatabaseCreating = false;
    },

    // Update database
    updateDatabaseRequest: (
      state,
      action: PayloadAction<UpdateDatabaseParams>
    ) => {
      state.isDatabaseUpdating = true;
    },
    updateDatabaseSuccess: (
      state,
      action: PayloadAction<UpdateDatabaseResponse>
    ) => {
      state.isDatabaseUpdating = false;
    },
    updateDatabaseFailure: (
      state,
      action: PayloadAction<{ params: UpdateDatabaseParams; error: string }>
    ) => {
      state.isDatabaseUpdating = false;
    },

    // Delete database
    deleteDatabaseRequest: (
      state,
      action: PayloadAction<DeleteDatabaseParams>
    ) => {
      state.isDatabaseDeleting = true;
    },
    deleteDatabaseSuccess: (
      state,
      action: PayloadAction<DeleteDatabaseResponse>
    ) => {
      state.database = null;
      state.isDatabaseDeleting = false;
    },
    deleteDatabaseFailure: (
      state,
      action: PayloadAction<{ params: DeleteDatabaseParams; error: string }>
    ) => {
      state.isDatabaseDeleting = false;
    },

    // Get management URL
    getManagementUrlRequest: (
      state,
      action: PayloadAction<GetManagementUrlParams>
    ) => {
      state.isDatabaseGettingManagementUrl = true;
    },
    getManagementUrlSuccess: (
      state,
      action: PayloadAction<GetManagementUrlResponse>
    ) => {
      state.dbManagementUrl = action.payload;
      state.isDatabaseGettingManagementUrl = false;
    },
    getManagementUrlFailure: (
      state,
      action: PayloadAction<{ params: GetManagementUrlParams; error: string }>
    ) => {
      state.isDatabaseGettingManagementUrl = false;
    },

    // Get DB service params
    getDBServiceParamsRequest: (
      state,
      action: PayloadAction<GetDBServiceParamsParams>
    ) => {
      state.areDBServiceParamsLoading = true;
    },
    getDBServiceParamsSuccess: (
      state,
      action: PayloadAction<GetDBServiceParamsResponse>
    ) => {
      state.DBServiceParams = action.payload;
      state.areDBServiceParamsLoading = false;
    },
    getDBServiceParamsFailure: (
      state,
      action: PayloadAction<{ params: GetDBServiceParamsParams; error: string }>
    ) => {
      state.areDBServiceParamsLoading = false;
    },

    // Get DBaaS quotas
    getDbaasQuotasRequest: (
      state,
      action: PayloadAction<GetDbaasQuotasParams>
    ) => {
      state.areDbaasQuotasLoading = true;
    },
    getDbaasQuotasSuccess: (
      state,
      action: PayloadAction<GetDbaasQuotasResponse>
    ) => {
      state.dbaasQuotas = action.payload;
      state.areDbaasQuotasLoading = false;
    },
    getDbaasQuotasFailure: (
      state,
      action: PayloadAction<{ params: GetDbaasQuotasParams; error: string }>
    ) => {
      state.areDbaasQuotasLoading = false;
    },

    // Get all services params
    getDBServicesParamsRequest: (
      state,
      action: PayloadAction<GetDBServicesParamsParams>
    ) => {
      state.areDBServicesParamsLoading = true;
    },
    getDBServicesParamsSuccess: (
      state,
      action: PayloadAction<GetDBServicesParamsResponse>
    ) => {
      state.DBServicesParams = action.payload;
      state.areDBServicesParamsLoading = false;
    },
    getDBServicesParamsFailure: (
      state,
      action: PayloadAction<{
        params: GetDBServicesParamsParams;
        error: string;
      }>
    ) => {
      state.areDBServicesParamsLoading = false;
    },

    // Change database status
    changeDatabaseStatusRequest: (
      state,
      action: PayloadAction<ChangeDatabaseStatusParams>
    ) => {
      state.isDatabaseStatusChanging = true;
    },
    changeDatabaseStatusSuccess: (
      state,
      action: PayloadAction<ChangeDatabaseStatusResponse>
    ) => {
      state.isDatabaseStatusChanging = false;
    },
    changeDatabaseStatusFailure: (
      state,
      action: PayloadAction<{
        params: ChangeDatabaseStatusParams;
        error: string;
      }>
    ) => {
      state.isDatabaseStatusChanging = false;
    },

    // Reset default user password
    resetDatabasePasswordRequest: (
      state,
      action: PayloadAction<ResetDatabasePasswordParams>
    ) => {
      state.isDatabasePasswordResetting = true;
    },
    resetDatabasePasswordSuccess: (
      state,
      action: PayloadAction<ResetDatabasePasswordResponse>
    ) => {
      state.databaseResetPassword = action.payload;
      state.isDatabasePasswordResetting = false;
    },
    resetDatabasePasswordFailure: (
      state,
      action: PayloadAction<{
        params: ResetDatabasePasswordParams;
        error: string;
      }>
    ) => {
      state.isDatabasePasswordResetting = false;
    },

    // Get database metrics
    getDatabaseMetricsRequest: (
      state,
      action: PayloadAction<GetDatabaseMetricsParams>
    ) => {
      state.isDatabaseMetricsLoading = true;
    },
    getDatabaseMetricsSuccess: (
      state,
      action: PayloadAction<GetDatabaseMetricsResponse>
    ) => {
      state.databaseMetrics = action.payload;
      state.isDatabaseMetricsLoading = false;
    },
    getDatabaseMetricsFailure: (
      state,
      action: PayloadAction<{
        params: GetDatabaseMetricsParams;
        error: string;
      }>
    ) => {
      state.isDatabaseMetricsLoading = false;
    },

    // Reset database user password
    resetDatabaseUserPasswordRequest: (
      state,
      action: PayloadAction<ResetDatabaseUserPasswordParams>
    ) => {
      state.isDatabasePasswordResetting = true;
    },
    resetDatabaseUserPasswordSuccess: (
      state,
      action: PayloadAction<ResetDatabaseUserPasswordResponse>
    ) => {
      state.databaseResetPassword = action.payload;
      state.isDatabasePasswordResetting = false;
    },
    resetDatabaseUserPasswordFailure: (
      state,
      action: PayloadAction<{
        params: ResetDatabaseUserPasswordParams;
        error: string;
      }>
    ) => {
      state.isDatabasePasswordResetting = false;
    },

    // Get database backup status
    getDatabaseBackupStatusRequest: (
      state,
      action: PayloadAction<GetDatabaseParams>
    ) => {
      state.isDatabaseBackupStatusLoading = true;
    },
    getDatabaseBackupStatusSuccess: (
      state,
      action: PayloadAction<GetDatabaseBackupStatusResponse>
    ) => {
      state.databaseBackupStatus = action.payload;
      state.isDatabaseBackupStatusLoading = false;
    },
    getDatabaseBackupStatusFailure: (
      state,
      action: PayloadAction<{ params: GetDatabaseParams; error: string }>
    ) => {
      state.isDatabaseBackupStatusLoading = false;
    },

    // Get database backup files
    getDatabaseBackupFilesRequest: (
      state,
      action: PayloadAction<GetDatabaseParams>
    ) => {
      state.areDatabaseBackupFilesLoading = true;
    },
    getDatabaseBackupFilesSuccess: (
      state,
      action: PayloadAction<GetDatabaseBackupFilesResponse>
    ) => {
      state.backupFilesError = null;
      state.databaseBackupFiles = action.payload.files;
      state.databaseBackupSets = action.payload.sets;
      state.areDatabaseBackupFilesLoading = false;
    },
    getDatabaseBackupFilesFailure: (
      state,
      action: PayloadAction<{ params: GetDatabaseParams; error: string }>
    ) => {
      state.areDatabaseBackupFilesLoading = false;
      state.backupFilesError = action.payload.error;
      state.databaseBackupFiles ??= [];
      state.databaseBackupSets ??= [];
    },

    // Get database custom options
    getDatabaseCustomOptionsRequest: (
      state,
      action: PayloadAction<GetDatabaseCustomOptionsParams>
    ) => {
      state.isDatabaseCustomOptionsLoading = true;
    },
    getDatabaseCustomOptionsSuccess: (
      state,
      action: PayloadAction<GetDatabaseCustomOptionsResponse>
    ) => {
      state.databaseCustomOptions = action.payload.custom_parameters ?? [];
      state.isDatabaseCustomOptionsLoading = false;
    },
    getDatabaseCustomOptionsFailure: (
      state,
      action: PayloadAction<{
        params: GetDatabaseCustomOptionsParams;
        error: string;
      }>
    ) => {
      state.isDatabaseCustomOptionsLoading = false;
      state.databaseCustomOptions ??= [];
    },

    // Get database encodings
    getDatabaseEncodingsRequest: (
      state,
      action: PayloadAction<GetDatabaseEncodingsParams>
    ) => {
      state.isDatabaseEncodingsLoading = true;
    },
    getDatabaseEncodingsSuccess: (
      state,
      action: PayloadAction<GetDatabaseEncodingsResponse>
    ) => {
      state.databaseEncodings = action.payload;
      state.isDatabaseEncodingsLoading = false;
    },
    getDatabaseEncodingsFailure: (
      state,
      action: PayloadAction<{
        params: GetDatabaseEncodingsParams;
        error: string;
      }>
    ) => {
      state.isDatabaseEncodingsLoading = false;
    },

    // Update database custom options
    updateDatabaseCustomOptionsRequest: (
      state,
      action: PayloadAction<UpdateDatabaseCustomOptionsParams>
    ) => {
      state.isDatabaseCustomOptionsUpdating = true;
    },
    updateDatabaseCustomOptionsSuccess: (
      state,
      action: PayloadAction<UpdateDatabaseCustomOptionsResponse>
    ) => {
      state.isDatabaseCustomOptionsUpdating = false;
    },
    updateDatabaseCustomOptionsFailure: (
      state,
      action: PayloadAction<{
        params: UpdateDatabaseCustomOptionsParams;
        error: string;
      }>
    ) => {
      state.isDatabaseCustomOptionsUpdating = false;
    },

    // Restore database
    restoreDatabaseRequest: (
      state,
      action: PayloadAction<RestoreDatabaseParams>
    ) => {
      state.isDatabaseRestoring = true;
    },
    restoreDatabaseSuccess: (
      state,
      action: PayloadAction<RestoreDatabaseResponse>
    ) => {
      state.isDatabaseRestoring = false;
    },
    restoreDatabaseFailure: (
      state,
      action: PayloadAction<{ params: RestoreDatabaseParams; error: string }>
    ) => {
      state.isDatabaseRestoring = false;
    },

    // Rollback restore
    rollbackRestoreRequest: (
      state,
      action: PayloadAction<RollbackRestoreParams>
    ) => {
      state.isDatabaseRollingBack = true;
    },
    rollbackRestoreSuccess: (
      state,
      action: PayloadAction<RollbackRestoreResponse>
    ) => {
      state.isDatabaseRollingBack = false;
    },
    rollbackRestoreFailure: (
      state,
      action: PayloadAction<{ params: RollbackRestoreParams; error: string }>
    ) => {
      state.isDatabaseRollingBack = false;
    },

    // Backup database
    backupDatabaseRequest: (
      state,
      action: PayloadAction<CreateBackupDatabaseParams>
    ) => {
      state.isDatabaseBackingUp = true;
    },
    backupDatabaseSuccess: (
      state,
      action: PayloadAction<CreateBackupDatabaseResponse>
    ) => {
      state.isDatabaseBackingUp = false;
    },
    backupDatabaseFailure: (
      state,
      action: PayloadAction<{
        params: CreateBackupDatabaseParams;
        error: string;
      }>
    ) => {
      state.isDatabaseBackingUp = false;
    },

    // Cron backup
    cronBackupRequest: (state, action: PayloadAction<CronBackupParams>) => {
      state.backupScheduleError = null;
      state.backupScheduleSaved = false;
      state.isCronBackupUpdating = true;
    },
    cronBackupSuccess: (state, action: PayloadAction<CronBackupResponse>) => {
      state.isCronBackupUpdating = false;
      state.backupScheduleSaved = true;
    },
    cronBackupFailure: (
      state,
      action: PayloadAction<{ params: CronBackupParams; error: string }>
    ) => {
      state.isCronBackupUpdating = false;
      state.backupScheduleError = action.payload.error;
    },

    // Cron delete
    clearBackupScheduleResult: (state) => {
      state.backupScheduleError = null;
      state.backupScheduleSaved = false;
    },
    cronDeleteRequest: (state, action: PayloadAction<CronDeleteParams>) => {
      state.isCronBackupDeleting = true;
    },
    cronDeleteSuccess: (state, action: PayloadAction<CronDeleteResponse>) => {
      state.isCronBackupDeleting = false;
    },
    cronDeleteFailure: (
      state,
      action: PayloadAction<{ params: CronDeleteParams; error: string }>
    ) => {
      state.isCronBackupDeleting = false;
    },

    openBackupStoragePicker: (
      state,
      action: PayloadAction<BackupStorageContext>
    ) => {
      state.backupStoragePicker = {
        ...initialBackupStoragePicker,
        context: action.payload
      };
    },
    closeBackupStoragePicker: (
      state,
      action: PayloadAction<BackupStorageContext>
    ) => {
      if (matchesBackupPicker(state, action.payload))
        state.backupStoragePicker = initialBackupStoragePicker;
    },
    backupStorageKeysRequest: (
      state,
      action: PayloadAction<BackupStorageContext>
    ) => {
      if (!matchesBackupPicker(state, action.payload)) return;
      state.backupStoragePicker.loadingKeys = true;
      state.backupStoragePicker.errors.keys = null;
    },
    backupStorageKeysSuccess: (
      state,
      action: PayloadAction<{
        context: BackupStorageContext;
        keys: Credentials[];
      }>
    ) => {
      if (!matchesBackupPicker(state, action.payload.context)) return;
      state.backupStoragePicker.loadingKeys = false;
      state.backupStoragePicker.credentials = action.payload.keys;
    },
    backupStorageKeysFailure: (
      state,
      action: PayloadAction<{ context: BackupStorageContext; error: string }>
    ) => {
      if (!matchesBackupPicker(state, action.payload.context)) return;
      state.backupStoragePicker.loadingKeys = false;
      state.backupStoragePicker.errors.keys = action.payload.error;
    },
    backupStorageKeyRequest: (
      state,
      action: PayloadAction<BackupStorageKeyContext>
    ) => {
      if (!matchesBackupPicker(state, action.payload)) return;
      const p = state.backupStoragePicker;
      if (p.credentialId !== action.payload.credsId) {
        p.details = null;
        p.buckets = null;
        p.createdBucketName = null;
      }
      p.credentialId = action.payload.credsId;
      p.loadingDetails = true;
      p.errors.details = null;
    },
    backupStorageKeySuccess: (
      state,
      action: PayloadAction<{
        context: BackupStorageKeyContext;
        key: Credentials;
      }>
    ) => {
      if (
        !matchesBackupPicker(state, action.payload.context) ||
        state.backupStoragePicker.credentialId !==
          action.payload.context.credsId
      )
        return;
      state.backupStoragePicker.loadingDetails = false;
      state.backupStoragePicker.details = action.payload.key;
    },
    backupStorageKeyFailure: (
      state,
      action: PayloadAction<{ context: BackupStorageKeyContext; error: string }>
    ) => {
      if (
        !matchesBackupPicker(state, action.payload.context) ||
        state.backupStoragePicker.credentialId !==
          action.payload.context.credsId
      )
        return;
      state.backupStoragePicker.loadingDetails = false;
      state.backupStoragePicker.errors.details = action.payload.error;
    },
    backupStorageBucketsRequest: (
      state,
      action: PayloadAction<BackupStorageKeyContext>
    ) => {
      if (
        !matchesBackupPicker(state, action.payload) ||
        state.backupStoragePicker.credentialId !== action.payload.credsId
      )
        return;
      state.backupStoragePicker.loadingBuckets = true;
      state.backupStoragePicker.errors.buckets = null;
    },
    backupStorageBucketsSuccess: (
      state,
      action: PayloadAction<{
        context: BackupStorageKeyContext;
        buckets: S3Bucket[];
      }>
    ) => {
      if (
        !matchesBackupPicker(state, action.payload.context) ||
        state.backupStoragePicker.credentialId !==
          action.payload.context.credsId
      )
        return;
      state.backupStoragePicker.loadingBuckets = false;
      state.backupStoragePicker.buckets = action.payload.buckets;
    },
    backupStorageBucketsFailure: (
      state,
      action: PayloadAction<{ context: BackupStorageKeyContext; error: string }>
    ) => {
      if (
        !matchesBackupPicker(state, action.payload.context) ||
        state.backupStoragePicker.credentialId !==
          action.payload.context.credsId
      )
        return;
      state.backupStoragePicker.loadingBuckets = false;
      state.backupStoragePicker.errors.buckets = action.payload.error;
    },
    createBackupStorageKeyRequest: (
      state,
      action: PayloadAction<BackupStorageContext>
    ) => {
      if (!matchesBackupPicker(state, action.payload)) return;
      state.backupStoragePicker.creatingKey = true;
      state.backupStoragePicker.createdCredentials = null;
      state.backupStoragePicker.errors.createKey = null;
    },
    createBackupStorageKeySuccess: (
      state,
      action: PayloadAction<{ context: BackupStorageContext; key: Credentials }>
    ) => {
      if (!matchesBackupPicker(state, action.payload.context)) return;
      state.backupStoragePicker.creatingKey = false;
      state.backupStoragePicker.createdCredentials = action.payload.key;
    },
    createBackupStorageKeyFailure: (
      state,
      action: PayloadAction<{ context: BackupStorageContext; error: string }>
    ) => {
      if (!matchesBackupPicker(state, action.payload.context)) return;
      state.backupStoragePicker.creatingKey = false;
      state.backupStoragePicker.errors.createKey = action.payload.error;
    },
    createBackupStorageBucketRequest: (
      state,
      action: PayloadAction<BackupBucketCreateParams>
    ) => {
      if (
        !matchesBackupPicker(state, action.payload) ||
        state.backupStoragePicker.credentialId !== action.payload.credsId
      )
        return;
      state.backupStoragePicker.creatingBucket = true;
      state.backupStoragePicker.createdBucketName = null;
      state.backupStoragePicker.errors.createBucket = null;
    },
    createBackupStorageBucketSuccess: (
      state,
      action: PayloadAction<BackupBucketCreateParams>
    ) => {
      if (
        !matchesBackupPicker(state, action.payload) ||
        state.backupStoragePicker.credentialId !== action.payload.credsId
      )
        return;
      state.backupStoragePicker.creatingBucket = false;
      state.backupStoragePicker.createdBucketName = action.payload.name;
    },
    createBackupStorageBucketFailure: (
      state,
      action: PayloadAction<{
        context: BackupBucketCreateParams;
        error: string;
      }>
    ) => {
      if (
        !matchesBackupPicker(state, action.payload.context) ||
        state.backupStoragePicker.credentialId !==
          action.payload.context.credsId
      )
        return;
      state.backupStoragePicker.creatingBucket = false;
      state.backupStoragePicker.errors.createBucket = action.payload.error;
    },

    // Link database to S3
    s3LinkDatabaseRequest: (
      state,
      action: PayloadAction<S3LinkDatabaseParams>
    ) => {
      state.s3LinkError = null;
      state.s3LinkSuccessful = false;
      state.isDatabaseS3Linking = true;
    },
    s3LinkDatabaseSuccess: (
      state,
      action: PayloadAction<S3LinkDatabaseResponse>
    ) => {
      state.isDatabaseS3Linking = false;
      state.s3LinkSuccessful = true;
    },
    s3LinkDatabaseFailure: (
      state,
      action: PayloadAction<{ params: S3LinkDatabaseParams; error: string }>
    ) => {
      state.isDatabaseS3Linking = false;
      state.s3LinkError = action.payload.error;
    },
    clearS3LinkResult: (state) => {
      state.s3LinkError = null;
      state.s3LinkSuccessful = false;
    },

    // Unlink database from S3
    s3UnlinkDatabaseRequest: (
      state,
      action: PayloadAction<S3UnlinkDatabaseParams>
    ) => {
      state.isDatabaseS3Unlinking = true;
    },
    s3UnlinkDatabaseSuccess: (
      state,
      action: PayloadAction<S3UnlinkDatabaseResponse>
    ) => {
      state.isDatabaseS3Unlinking = false;
    },
    s3UnlinkDatabaseFailure: (
      state,
      action: PayloadAction<{ params: S3UnlinkDatabaseParams; error: string }>
    ) => {
      state.isDatabaseS3Unlinking = false;
    },

    // Create replica database
    createReplicaDatabaseRequest: (
      state,
      action: PayloadAction<CreateReplicaDatabaseParams>
    ) => {
      state.isReplicaDatabaseCreating = true;
    },
    createReplicaDatabaseSuccess: (
      state,
      action: PayloadAction<CreateReplicaDatabaseResponse>
    ) => {
      state.replicaDatabase = action.payload;
      state.isReplicaDatabaseCreating = false;
    },
    createReplicaDatabaseFailure: (
      state,
      action: PayloadAction<{
        params: CreateReplicaDatabaseParams;
        error: string;
      }>
    ) => {
      state.isReplicaDatabaseCreating = false;
    },

    // Get replica databases
    getReplicaDatabasesRequest: (
      state,
      action: PayloadAction<GetReplicaDatabaseParams>
    ) => {
      state.areReplicaDatabasesLoading = true;
    },
    getReplicaDatabasesSuccess: (
      state,
      action: PayloadAction<GetReplicaDatabasesResponse>
    ) => {
      state.replicaDatabases = action.payload;
      state.areReplicaDatabasesLoading = false;
    },
    getReplicaDatabasesFailure: (
      state,
      action: PayloadAction<{
        params: GetReplicaDatabaseParams;
        error: string;
      }>
    ) => {
      state.areReplicaDatabasesLoading = false;
      state.replicaDatabases ??= [];
    },

    // Get database children
    getDatabaseChildrenRequest: (
      state,
      action: PayloadAction<GetDatabaseChildrenParams>
    ) => {
      state.isDatabaseChildrenLoading = true;
    },
    getDatabaseChildrenSuccess: (
      state,
      action: PayloadAction<GetDatabaseChildrenResponse>
    ) => {
      state.databaseChildren = action.payload;
      state.isDatabaseChildrenLoading = false;
    },
    getDatabaseChildrenFailure: (
      state,
      action: PayloadAction<{
        params: GetDatabaseChildrenParams;
        error: string;
      }>
    ) => {
      state.isDatabaseChildrenLoading = false;
      state.databaseChildren ??= [];
    },

    // Get database users
    getDatabaseUsersRequest: (
      state,
      action: PayloadAction<GetDatabaseUsersParams>
    ) => {
      state.isDatabaseUsersLoading = true;
    },
    getDatabaseUsersSuccess: (
      state,
      action: PayloadAction<GetDatabaseUsersResponse>
    ) => {
      state.databaseUsers = action.payload;
      state.isDatabaseUsersLoading = false;
    },
    getDatabaseUsersFailure: (
      state,
      action: PayloadAction<{ params: GetDatabaseUsersParams; error: string }>
    ) => {
      state.isDatabaseUsersLoading = false;
      state.databaseUsers ??= [];
    },

    // Create database child
    createDatabaseChildRequest: (
      state,
      action: PayloadAction<CreateDatabaseChildParams>
    ) => {
      state.isDatabaseChildrenCreating = true;
    },
    createDatabaseChildSuccess: (
      state,
      action: PayloadAction<CreateDatabaseChildResponse>
    ) => {
      state.isDatabaseChildrenCreating = false;
    },
    createDatabaseChildFailure: (
      state,
      action: PayloadAction<{
        params: CreateDatabaseChildParams;
        error: string;
      }>
    ) => {
      state.isDatabaseChildrenCreating = false;
    },

    // Create database user
    createDatabaseUserRequest: (
      state,
      action: PayloadAction<CreateDatabaseUserParams>
    ) => {
      state.isDatabaseUsersCreating = true;
    },
    createDatabaseUserSuccess: (
      state,
      action: PayloadAction<CreateDatabaseUserResponse>
    ) => {
      state.databaseUser = action.payload;
      state.isDatabaseUsersCreating = false;
    },
    createDatabaseUserFailure: (
      state,
      action: PayloadAction<{ params: CreateDatabaseUserParams; error: string }>
    ) => {
      state.isDatabaseUsersCreating = false;
    },

    // Delete database child
    deleteDatabaseChildRequest: (
      state,
      action: PayloadAction<DeleteDatabaseChildParams>
    ) => {
      state.isDatabaseChildrenDeleting = true;
    },
    deleteDatabaseChildSuccess: (
      state,
      action: PayloadAction<{ params: DeleteDatabaseChildParams }>
    ) => {
      state.databaseChildren = state.databaseChildren
        ? state.databaseChildren.filter(
            (db) => db.name !== action.payload.params.dbName
          )
        : null;
      state.isDatabaseChildrenDeleting = false;
    },
    deleteDatabaseChildFailure: (
      state,
      action: PayloadAction<{
        params: DeleteDatabaseChildParams;
        error: string;
      }>
    ) => {
      state.isDatabaseChildrenDeleting = false;
    },

    // Update database child
    updateDatabaseChildRequest: (
      state,
      action: PayloadAction<UpdateDatabaseChildParams>
    ) => {
      state.isDatabaseChildUpdating = true;
    },
    updateDatabaseChildSuccess: (
      state,
      action: PayloadAction<{ params: UpdateDatabaseChildParams }>
    ) => {
      state.isDatabaseChildUpdating = false;
    },
    updateDatabaseChildFailure: (
      state,
      action: PayloadAction<{
        params: UpdateDatabaseChildParams;
        error: string;
      }>
    ) => {
      state.isDatabaseChildUpdating = false;
    },

    // Delete database user
    deleteDatabaseUserRequest: (
      state,
      action: PayloadAction<DeleteDatabaseUserParams>
    ) => {
      state.isDatabaseUsersDeleting = true;
    },
    deleteDatabaseUserSuccess: (
      state,
      action: PayloadAction<{ params: DeleteDatabaseUserParams }>
    ) => {
      state.databaseUsers = state.databaseUsers
        ? state.databaseUsers.filter(
            (user) => user.name !== action.payload.params.username
          )
        : null;
      state.isDatabaseUsersDeleting = false;
    },
    deleteDatabaseUserFailure: (
      state,
      action: PayloadAction<{ params: DeleteDatabaseUserParams; error: string }>
    ) => {
      state.isDatabaseUsersDeleting = false;
    },

    // Set database user permissions
    setDatabaseUserPermissionsRequest: (
      state,
      action: PayloadAction<SetDatabaseUserPermissionsParams>
    ) => {
      state.isDatabaseUsersPermissionsUpdating = true;
    },
    setDatabaseUserPermissionsSuccess: (state) => {
      state.isDatabaseUsersPermissionsUpdating = false;
    },
    setDatabaseUserPermissionsFailure: (
      state,
      action: PayloadAction<{
        params: SetDatabaseUserPermissionsParams;
        error: string;
      }>
    ) => {
      state.isDatabaseUsersPermissionsUpdating = false;
    },

    // Clear helpers
    clearDatabase: (state) => {
      state.database = null;
    },
    clearDatabaseResetPassword: (state) => {
      state.databaseResetPassword = null;
    },
    clearDatabaseUser: (state) => {
      state.databaseUser = null;
    },
    clearReplicaDatabases: (state) => {
      state.replicaDatabases = null;
    },
    clearDatabaseUsers: (state) => {
      state.databaseUsers = null;
    },
    clearDatabaseChildren: (state) => {
      state.databaseChildren = null;
    },
    clearBackupStatus: (state) => {
      state.databaseBackupStatus = null;
    },
    clearBackupFiles: (state) => {
      state.backupFilesError = null;
      state.databaseBackupFiles = null;
      state.databaseBackupSets = null;
    },
    clearCreatedDatabasePassword: (state) => {
      state.createdDatabasePassword = null;
    },
    clear: () => initialState
  }
});

export const {
  getDatabaseRequest,
  getDatabaseSuccess,
  getDatabaseFailure,
  getDatabasesRequest,
  getDatabasesSuccess,
  getDatabasesFailure,
  createDatabaseRequest,
  createDatabaseSuccess,
  createDatabaseFailure,
  updateDatabaseRequest,
  updateDatabaseSuccess,
  updateDatabaseFailure,
  deleteDatabaseRequest,
  deleteDatabaseSuccess,
  deleteDatabaseFailure,
  getManagementUrlRequest,
  getManagementUrlSuccess,
  getManagementUrlFailure,
  getDBServiceParamsRequest,
  getDBServiceParamsSuccess,
  getDBServiceParamsFailure,
  getDbaasQuotasRequest,
  getDbaasQuotasSuccess,
  getDbaasQuotasFailure,
  getDBServicesParamsRequest,
  getDBServicesParamsSuccess,
  getDBServicesParamsFailure,
  changeDatabaseStatusRequest,
  changeDatabaseStatusSuccess,
  changeDatabaseStatusFailure,
  resetDatabasePasswordRequest,
  resetDatabasePasswordSuccess,
  resetDatabasePasswordFailure,
  getDatabaseMetricsRequest,
  getDatabaseMetricsSuccess,
  getDatabaseMetricsFailure,
  rollbackRestoreRequest,
  rollbackRestoreSuccess,
  rollbackRestoreFailure,
  resetDatabaseUserPasswordRequest,
  resetDatabaseUserPasswordSuccess,
  resetDatabaseUserPasswordFailure,
  getDatabaseBackupStatusRequest,
  getDatabaseBackupStatusSuccess,
  getDatabaseBackupStatusFailure,
  getDatabaseBackupFilesRequest,
  getDatabaseBackupFilesSuccess,
  getDatabaseBackupFilesFailure,
  getDatabaseCustomOptionsRequest,
  getDatabaseCustomOptionsSuccess,
  getDatabaseCustomOptionsFailure,
  getDatabaseEncodingsRequest,
  getDatabaseEncodingsSuccess,
  getDatabaseEncodingsFailure,
  updateDatabaseCustomOptionsRequest,
  updateDatabaseCustomOptionsSuccess,
  updateDatabaseCustomOptionsFailure,
  restoreDatabaseRequest,
  restoreDatabaseSuccess,
  restoreDatabaseFailure,
  backupDatabaseRequest,
  backupDatabaseSuccess,
  backupDatabaseFailure,
  cronBackupRequest,
  cronBackupSuccess,
  cronBackupFailure,
  cronDeleteRequest,
  cronDeleteSuccess,
  cronDeleteFailure,
  s3LinkDatabaseRequest,
  s3LinkDatabaseSuccess,
  s3LinkDatabaseFailure,
  s3UnlinkDatabaseRequest,
  s3UnlinkDatabaseSuccess,
  s3UnlinkDatabaseFailure,
  createReplicaDatabaseRequest,
  createReplicaDatabaseSuccess,
  createReplicaDatabaseFailure,
  getReplicaDatabasesRequest,
  getReplicaDatabasesSuccess,
  getReplicaDatabasesFailure,
  getDatabaseChildrenRequest,
  getDatabaseChildrenSuccess,
  getDatabaseChildrenFailure,
  getDatabaseUsersRequest,
  getDatabaseUsersSuccess,
  getDatabaseUsersFailure,
  createDatabaseChildRequest,
  createDatabaseChildSuccess,
  createDatabaseChildFailure,
  createDatabaseUserRequest,
  createDatabaseUserSuccess,
  createDatabaseUserFailure,
  deleteDatabaseChildRequest,
  deleteDatabaseChildSuccess,
  deleteDatabaseChildFailure,
  updateDatabaseChildRequest,
  updateDatabaseChildSuccess,
  updateDatabaseChildFailure,
  deleteDatabaseUserRequest,
  deleteDatabaseUserSuccess,
  deleteDatabaseUserFailure,
  setDatabaseUserPermissionsRequest,
  setDatabaseUserPermissionsSuccess,
  setDatabaseUserPermissionsFailure,
  clearDatabase,
  clearDatabaseResetPassword,
  clearDatabaseUser,
  clearReplicaDatabases,
  clearDatabaseUsers,
  clearDatabaseChildren,
  clearBackupStatus,
  clearBackupFiles,
  clearCreatedDatabasePassword,
  clear
} = databasesSlice.actions;

export const { actions: databasesActions } = databasesSlice;

export const reducer = databasesSlice.reducer;
